import Highcharts from 'highcharts/esm/highcharts.js';
import { mapAllDateSeries } from './date-series/map-all-date-series';
import { mapNumericSeries } from './numeric-series/map-numeric-series';
import { FieldDefinitionDto, FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import { TimelinePanelDto } from '@interfaces-clean/subject-monitoring.interface';
import {
    TimelineAxisSide,
    TimelineDateSeries,
    TimelineNumericSeries,
    TimelineSeriesCustomData,
} from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/shared';
import {
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis,
} from '@queries/generated/central-monitoring.generated';

type MapAxisDataToSeriesOptions = {
    axis:
        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis
        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis;
    side: TimelineAxisSide;
    data: TimelinePanelDto['string'];
    getNextThemeColor: () => string;
    getNextSeriesIndex: () => number;
    fieldDefinitions: FieldDefinitionDto[];
    pointEvents?: Highcharts.PointEventsOptionsObject;
};

export function mapAxisDataToSeries({
    axis,
    side,
    data,
    getNextThemeColor,
    getNextSeriesIndex,
    fieldDefinitions,
    pointEvents,
}: MapAxisDataToSeriesOptions): (Highcharts.SeriesOptionsType & TimelineSeriesCustomData)[] {
    if (axis.numericSeries != null) {
        // Group each series with its data
        const numericSeriesWithData: TimelineNumericSeries[] = (axis.numericSeries ?? []).map((ds) => ({
            series: ds,
            data: data?.[Object.keys(data)[getNextSeriesIndex()]] ?? [],
        }));

        const floatFieldKeys = new Set(
            fieldDefinitions
                .filter((f) => f.fieldType === FieldDefinitionFieldTypeDto.FLOAT) // Get float fields
                .map((f) => f.key),
        );

        // Map each axis series to 1 to 3 highcharts series [parent (always), upper threshold (sometimes), lower threshold (sometimes)], and flatten
        return numericSeriesWithData.flatMap((ns, i) =>
            mapNumericSeries({
                id: `${side}_series${i}`,
                series: ns,
                side,
                themeColor: getNextThemeColor(),
                isFloat: floatFieldKeys.has(ns.series.yPlotField),
                unit: axis.unit ?? undefined,
                pointEvents,
            }),
        );
    }
    if (axis.dateSeries != null) {
        // Group each series with its data
        const dateSeriesWithData: TimelineDateSeries[] = (axis.dateSeries ?? []).map((ds) => ({
            series: ds,
            data: data?.[Object.keys(data)[getNextSeriesIndex()]] ?? [],
        }));

        return mapAllDateSeries({ allSeries: dateSeriesWithData, side, pointEvents });
    }

    throw new Error(`Axis (${side}) has no series!`);
}
